/** Shared modal chrome (header + footer). */
export const LEARNER_SETTINGS_MODAL_CHROME_CLASS =
  'border-slate-300/95 bg-gradient-to-r from-slate-200 via-slate-100 to-violet-200/90 shadow-[inset_0_1px_0_rgba(255,255,255,0.65)] dark:border-slate-600/90 dark:from-slate-900 dark:via-gray-900 dark:to-violet-950/55 dark:shadow-none';

export type LearnerSettingsSectionThemeId = 'sky' | 'amber' | 'violet' | 'emerald';

export interface LearnerSettingsSectionTheme {
  pillRule: string;
  pill: string;
  section: string;
  icon: string;
  saveButton: string;
  accentSelected: string;
  accentCheck: string;
  checkbox: string;
  checkboxFocus: string;
  optionHoverBorder: string;
  fontCardSelected: string;
  rangeAccent: string;
  loader: string;
  badge: string;
  iconAccent: string;
}

export const LEARNER_SETTINGS_SECTION_THEMES: Record<LearnerSettingsSectionThemeId, LearnerSettingsSectionTheme> = {
  sky: {
    pillRule: 'h-px flex-1 rounded-full bg-gradient-to-r from-transparent via-sky-500/80 to-transparent dark:via-sky-600/65',
    pill:
      'max-w-[min(100%,28rem)] shrink-0 truncate rounded-full border border-sky-400/90 bg-gradient-to-r from-sky-300/95 via-sky-200/90 to-cyan-300/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-sky-950 shadow-md shadow-sky-900/10 dark:border-sky-700/80 dark:from-sky-900/95 dark:via-sky-950 dark:to-cyan-950/80 dark:text-sky-100',
    section:
      'rounded-2xl border border-sky-300/80 bg-gradient-to-br from-sky-50/95 via-white to-cyan-50/70 shadow-md shadow-sky-200/50 dark:border-sky-800/70 dark:from-sky-950/40 dark:via-gray-800/95 dark:to-cyan-950/35 dark:shadow-black/30 overflow-hidden',
    icon: 'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-sky-500 to-cyan-600 text-white shadow-lg shadow-sky-500/35',
    saveButton:
      'rounded-xl bg-gradient-to-r from-sky-600 to-cyan-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-sky-600/25 transition-colors hover:from-sky-700 hover:to-cyan-700 disabled:opacity-50',
    accentSelected: 'bg-sky-50 dark:bg-sky-950/45 text-sky-900 dark:text-sky-100',
    accentCheck: 'text-sky-600 dark:text-sky-400',
    checkbox: 'text-sky-600',
    checkboxFocus: 'focus:ring-sky-500',
    optionHoverBorder: 'hover:border-sky-300 dark:hover:border-sky-700',
    fontCardSelected: 'border-sky-500 bg-sky-50/90 dark:bg-sky-950/50 dark:border-sky-400 shadow-sm ring-1 ring-sky-500/25',
    rangeAccent: 'accent-sky-600',
    loader: 'text-sky-600 dark:text-sky-400',
    badge: 'bg-sky-100 text-sky-800 dark:bg-sky-950/60 dark:text-sky-200',
    iconAccent: 'text-sky-500'
  },
  amber: {
    pillRule: 'h-px flex-1 rounded-full bg-gradient-to-r from-transparent via-amber-500/80 to-transparent dark:via-amber-600/65',
    pill:
      'max-w-[min(100%,28rem)] shrink-0 truncate rounded-full border border-amber-400/90 bg-gradient-to-r from-amber-300/95 via-amber-200/90 to-orange-300/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-amber-950 shadow-md shadow-amber-900/10 dark:border-amber-700/80 dark:from-amber-900/95 dark:via-amber-950 dark:to-orange-950/80 dark:text-amber-100',
    section:
      'rounded-2xl border border-amber-300/80 bg-gradient-to-br from-amber-50/95 via-white to-orange-50/60 shadow-md shadow-amber-200/50 dark:border-amber-800/70 dark:from-amber-950/40 dark:via-gray-800/95 dark:to-orange-950/30 dark:shadow-black/30 overflow-hidden',
    icon: 'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 text-white shadow-lg shadow-amber-500/35',
    saveButton:
      'rounded-xl bg-gradient-to-r from-amber-600 to-orange-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-amber-600/25 transition-colors hover:from-amber-700 hover:to-orange-700 disabled:opacity-50',
    accentSelected: 'bg-amber-50 dark:bg-amber-950/45 text-amber-900 dark:text-amber-100',
    accentCheck: 'text-amber-600 dark:text-amber-400',
    checkbox: 'text-amber-600',
    checkboxFocus: 'focus:ring-amber-500',
    optionHoverBorder: 'hover:border-amber-300 dark:hover:border-amber-700',
    fontCardSelected: 'border-amber-500 bg-amber-50/90 dark:bg-amber-950/50 dark:border-amber-400 shadow-sm ring-1 ring-amber-500/25',
    rangeAccent: 'accent-amber-600',
    loader: 'text-amber-600 dark:text-amber-400',
    badge: 'bg-amber-100 text-amber-900 dark:bg-amber-950/60 dark:text-amber-200',
    iconAccent: 'text-amber-500'
  },
  violet: {
    pillRule: 'h-px flex-1 rounded-full bg-gradient-to-r from-transparent via-violet-500/80 to-transparent dark:via-violet-600/65',
    pill:
      'max-w-[min(100%,28rem)] shrink-0 truncate rounded-full border border-violet-400/90 bg-gradient-to-r from-violet-300/95 via-violet-200/90 to-indigo-300/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-violet-950 shadow-md shadow-violet-900/10 dark:border-violet-700/80 dark:from-violet-900/95 dark:via-violet-950 dark:to-indigo-950/80 dark:text-violet-100',
    section:
      'rounded-2xl border border-violet-300/80 bg-gradient-to-br from-violet-50/95 via-white to-indigo-50/65 shadow-md shadow-violet-200/50 dark:border-violet-800/70 dark:from-violet-950/40 dark:via-gray-800/95 dark:to-indigo-950/35 dark:shadow-black/30 overflow-hidden',
    icon: 'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-violet-500 to-indigo-600 text-white shadow-lg shadow-violet-500/35',
    saveButton:
      'rounded-xl bg-gradient-to-r from-violet-600 to-indigo-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-violet-600/25 transition-colors hover:from-violet-700 hover:to-indigo-700 disabled:opacity-50',
    accentSelected: 'bg-violet-50 dark:bg-violet-950/45 text-violet-900 dark:text-violet-100',
    accentCheck: 'text-violet-600 dark:text-violet-400',
    checkbox: 'text-violet-600',
    checkboxFocus: 'focus:ring-violet-500',
    optionHoverBorder: 'hover:border-violet-300 dark:hover:border-violet-700',
    fontCardSelected: 'border-violet-500 bg-violet-50/90 dark:bg-violet-950/50 dark:border-violet-400 shadow-sm ring-1 ring-violet-500/25',
    rangeAccent: 'accent-violet-600',
    loader: 'text-violet-600 dark:text-violet-400',
    badge: 'bg-violet-100 text-violet-900 dark:bg-violet-950/60 dark:text-violet-200',
    iconAccent: 'text-violet-500'
  },
  emerald: {
    pillRule: 'h-px flex-1 rounded-full bg-gradient-to-r from-transparent via-emerald-500/80 to-transparent dark:via-emerald-600/65',
    pill:
      'max-w-[min(100%,28rem)] shrink-0 truncate rounded-full border border-emerald-400/90 bg-gradient-to-r from-emerald-300/95 via-emerald-200/90 to-teal-300/85 px-3 py-1 text-[10px] font-bold uppercase tracking-wider text-emerald-950 shadow-md shadow-emerald-900/10 dark:border-emerald-700/80 dark:from-emerald-900/95 dark:via-emerald-950 dark:to-teal-950/80 dark:text-emerald-100',
    section:
      'rounded-2xl border border-emerald-300/80 bg-gradient-to-br from-emerald-50/95 via-white to-teal-50/60 shadow-md shadow-emerald-200/50 dark:border-emerald-800/70 dark:from-emerald-950/40 dark:via-gray-800/95 dark:to-teal-950/30 dark:shadow-black/30 overflow-hidden',
    icon: 'flex h-12 w-12 shrink-0 items-center justify-center rounded-2xl bg-gradient-to-br from-emerald-500 to-teal-600 text-white shadow-lg shadow-emerald-500/35',
    saveButton:
      'rounded-xl bg-gradient-to-r from-emerald-600 to-teal-600 px-6 py-2.5 text-sm font-semibold text-white shadow-md shadow-emerald-600/25 transition-colors hover:from-emerald-700 hover:to-teal-700 disabled:opacity-50',
    accentSelected: 'bg-emerald-50 dark:bg-emerald-950/45 text-emerald-900 dark:text-emerald-100',
    accentCheck: 'text-emerald-600 dark:text-emerald-400',
    checkbox: 'text-emerald-600',
    checkboxFocus: 'focus:ring-emerald-500',
    optionHoverBorder: 'hover:border-emerald-300 dark:hover:border-emerald-700',
    fontCardSelected: 'border-emerald-500 bg-emerald-50/90 dark:bg-emerald-950/50 dark:border-emerald-400 shadow-sm ring-1 ring-emerald-500/25',
    rangeAccent: 'accent-emerald-600',
    loader: 'text-emerald-600 dark:text-emerald-400',
    badge: 'bg-emerald-100 text-emerald-900 dark:bg-emerald-950/60 dark:text-emerald-200',
    iconAccent: 'text-emerald-500'
  }
};
